import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DooctiService } from '@/app/core/services/doocti/doocti.service';

/**
 * Floating container for the Doocti CTI iframe.
 *
 * Renders a fixed-position panel (bottom-right) with a toggle button.
 * The actual iframe is created and managed by `DooctiService`;
 * this component only provides the DOM container and UI chrome.
 */
@Component({
  selector: 'app-doocti-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './doocti-container.component.html',
  styleUrl: './doocti-container.component.scss',
})
export class DooctiContainerComponent implements OnInit, OnDestroy {
  private readonly dooctiService = inject(DooctiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('iframeContainer', { static: true })
  iframeContainerRef!: ElementRef<HTMLDivElement>;

  isVisible = true;
  isReady = false;
  isReconnecting = false;

  ngOnInit(): void {
    // Attach the DOM container to the service so it can append the iframe.
    this.dooctiService.attachContainer(this.iframeContainerRef.nativeElement);

    // Subscribe to visibility changes.
    this.dooctiService.isVisible$.pipe(takeUntil(this.destroy$)).subscribe((visible) => {
      this.isVisible = visible;
      this.cdr.detectChanges();
    });

    // Subscribe to readiness state.
    this.dooctiService.isReady$.pipe(takeUntil(this.destroy$)).subscribe((ready) => {
      this.isReady = ready;
      this.cdr.detectChanges();
    });

    // Subscribe to reconnecting / force-logout state.
    this.dooctiService.isReconnecting$.pipe(takeUntil(this.destroy$)).subscribe((reconnecting) => {
      this.isReconnecting = reconnecting;
      this.cdr.detectChanges();
    });
  }

  togglePanel(): void {
    this.dooctiService.toggleVisibility();
  }

  reconnect(): void {
    this.dooctiService.reconnect().subscribe();
  }

  triggerCallEnd(): void {
    this.dooctiService.notifyCallEnded();
  }

  ngOnDestroy(): void {
    this.dooctiService.detachContainer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
